import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { fetchPDFUrl, fetchSourcePDFUrl, fetchContractsForSource } from '../services/api';

const PDFViewer = ({ contract, mode = 'contract', onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [derivedContracts, setDerivedContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(true);

  const isSourceMode = mode === 'source';

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let url;
        if (isSourceMode) {
          if (!contract?.source?.file_path) {
            setError('No source file available for this contract');
            setLoading(false);
            return;
          }
          url = await fetchSourcePDFUrl(contract.source.file_path);
        } else {
          if (!contract?.contract_file) {
            setError('No PDF file available for this contract');
            setLoading(false);
            return;
          }
          url = await fetchPDFUrl(contract._id);
        }
        
        setPdfUrl(url);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(`Failed to load ${isSourceMode ? 'source' : 'contract'} PDF from Dropbox.`);
      } finally {
        setLoading(false);
      }
    };

    const loadDerivedContracts = async () => {
      if (!isSourceMode || !contract?.source?._id) {
        setContractsLoading(false);
        return;
      }

      try {
        setContractsLoading(true);
        console.log('[DEBUG] Loading derived contracts for source ID:', contract.source._id);
        const contracts = await fetchContractsForSource(contract.source._id);
        console.log('[DEBUG] Received derived contracts:', contracts);
        setDerivedContracts(contracts);
      } catch (err) {
        console.error('Error loading derived contracts:', err);
      } finally {
        setContractsLoading(false);
      }
    };

    loadPDF();
    if (isSourceMode) {
      loadDerivedContracts();
    }
  }, [contract, isSourceMode]);

  return (
    <div className="h-full w-full flex">
      {/* Side Panel with Details - LEFT SIDE */}
      <div className="w-96 bg-gray-50 border-r border-gray-200 p-6 overflow-y-auto">
          {/* Header */}
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              {isSourceMode ? 'Derived Contracts' : 'Contract Details'}
            </h4>
            {isSourceMode && (
              <p className="text-sm text-gray-600 mt-1">
                Contracts generated from this source document
              </p>
            )}
          </div>
          
          <div className="space-y-4">
            {isSourceMode ? (
              // Source Mode: Show derived contracts (simplified)
              <>
                {/* Source Info */}
                <div>
                  <div className="text-sm text-gray-600 mb-1">
                    <strong>Source File:</strong>
                  </div>
                  <div className="text-sm text-gray-900 break-all">
                    {contract.source?.file_path?.split('/').pop() || 'Unknown'}
                  </div>
                </div>

                {/* Derived Contracts List */}
                {contractsLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : derivedContracts.length > 0 ? (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Generated Contracts ({derivedContracts.length})
                    </h5>
                    <div className="space-y-2">
                      {derivedContracts.map((derivedContract) => (
                        <div key={derivedContract._id} className="bg-white p-3 rounded border text-sm">
                          <div className="font-medium">
                            Pages {derivedContract.pages}
                          </div>
                          <div className="text-gray-600">
                            {derivedContract.publicationName} • {derivedContract.publicationDate}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">
                    No derived contracts found for this source document.
                  </div>
                )}
              </>
            ) : (
              // Contract Mode: Show contract details
              <>
            {/* Contract Info */}
            <div>
              <div className="text-sm text-gray-600 mb-1">Pages: {contract.pages}</div>
              <div className="text-sm text-gray-600 mb-1">{contract.publicationName}</div>
              <div className="text-sm text-gray-600">{contract.publicationDate}</div>
            </div>

            {/* Works */}
            {contract.works && contract.works.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Works</h5>
                <div className="space-y-2">
                  {contract.works.map((work, index) => (
                    <div key={index} className="bg-white p-3 rounded border text-sm">
                      <div className="font-medium">{work.workName}</div>
                      <div className="text-gray-600">
                        Type: {work.workType}
                        {work.workFee && (
                          <div>Fee: {work.workFee}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contributors */}
            {contract.contributors && contract.contributors.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Contributors</h5>
                <div className="flex flex-wrap gap-2">
                  {contract.contributors.map((contributor, index) => (
                    <span
                      key={index}
                      className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                    >
                      {contributor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Rights Level */}
            {contract.rightsLevel && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Rights Level</h5>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      contract.rightsLevel.level === 'A' ? 'bg-green-100 text-green-800' :
                      contract.rightsLevel.level === 'B' ? 'bg-blue-100 text-blue-800' :
                      contract.rightsLevel.level === 'C' ? 'bg-yellow-100 text-yellow-800' :
                      contract.rightsLevel.level === 'D' ? 'bg-orange-100 text-orange-800' :
                      contract.rightsLevel.level === 'E' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      Level {contract.rightsLevel.level}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {contract.rightsLevel.justification}
                  </div>
                </div>
              </div>
            )}

            {/* Rights */}
            {contract.rights && contract.rights.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Rights</h5>
                <div className="bg-white p-3 rounded border">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {contract.rights.map((right, index) => (
                      <li key={index}>{right}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Notes */}
            {contract.notes && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Notes</h5>
                <div className="bg-white p-3 rounded border text-sm">
                  {contract.notes}
                </div>
              </div>
            )}

            {/* Source Information */}
            {contract.source && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Source</h5>
                <div className="bg-white p-3 rounded border text-sm">
                  <div className="break-all">{contract.source.file_path}</div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* PDF Viewer Panel */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {isSourceMode ? 'Source Document' : `Contract PDF - Pages ${contract.pages}`}
              </h3>
              <p className="text-sm text-gray-500">
                {isSourceMode 
                  ? (contract.source?.file_path?.split('/').pop() || 'Unknown source')
                  : `${contract.publicationName} • ${contract.publicationDate}`
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* PDF Content */}
          <div className="flex-1 p-4">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-gray-600">
                  Loading {isSourceMode ? 'source' : 'contract'} PDF...
                </span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-red-600 text-lg font-medium mb-2">
                    Unable to load PDF
                  </div>
                  <div className="text-gray-600 text-sm mb-4">
                    {error}
                  </div>
                  <div className="text-xs text-gray-500">
                    Contract file: {contract.contract_file}
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && pdfUrl && (
              <div className="h-full">
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0 rounded"
                  title="Contract PDF"
                  onError={() => {
                    setError('Failed to display PDF.');
                  }}
                />
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default PDFViewer;